import type { IDomEditor } from '@wangeditor/editor'
import { addHost, contentRemoveHost } from '@qctsw/common'
import { isString } from '@qctsw/utils'
import { UPLOAD_URL } from '@/shared/constants'
import { FILE_UPLOAD_TYPE } from '@/shared/types'

type InsertFnType = (url: string, alt: string, href: string) => void

export const AllowHostList = [
  'qcwp.com',
  'qctsw.com',
  'www.qcwp.com',
  'www.qctsw.com',
  'cnsaiche.com',
  'www.cnsaiche.com',
]

export function editorComponent() {
  const toolbarConfig = {
    excludeKeys: [
      'headerSelect',
      'italic',
      'group-more-style', // 排除菜单组，写菜单组 key 的值即可
      'fullScreen',
      'fontSize',
      'fontFamily',
      'lineHeight',
      'group-image',
      'group-video',
      'emotion',
      'todo',
      'insertTable',
      'codeBlock',
      'insertLink',
      'color',
      'bgColor',
    ],
  }
  const hoverbarKeys = {
    text: {
      menuKeys: [
        'bulletedList',
        '|',
        'bold',
        'through',
        'clearStyle',
      ],
    },

  }
  const editorConfig = {
    placeholder: '请输入内容...',
    // customPaste: (editor: IDomEditor, e: ClipboardEvent) => {

    // },
    MENU_CONF: {
      insertLink: {
        checkLink: customCheckLinkFn, // 也支持 async 函数
        parseLinkUrl: customParseLinkUrl, // 也支持 async 函数
      },
      editLink: {
        checkLink: customCheckLinkFn, // 也支持 async 函数
        parseLinkUrl: customParseLinkUrl, // 也支持 async 函数
      },
      uploadImage: {
        server: `/api${UPLOAD_URL}`,
        // form-data fieldName ，默认值 "wangeditor-uploaded-image"
        fieldName: 'file',
        // 单个文件的最大体积限制，默认为 2M
        maxFileSize: 5 * 1024 * 1024, // 1M
        // 最多可上传几个文件，默认为 100
        maxNumberOfFiles: 20,
        // 选择文件时的类型限制，默认为 ["image/*"] 。如不想限制，则设置为 []
        allowedFileTypes: ['image/*'],
        // 自定义上传参数，例如传递验证的 token 等。参数会被添加到 formData 中，一起上传到服务端。
        meta: {
          code: FILE_UPLOAD_TYPE.complain,
        },
        // 将 meta 拼接到 url 参数中，默认 false
        metaWithUrl: false,

        // 跨域是否传递 cookie ，默认为 false
        withCredentials: true,
        // 超时时间，默认为 10 秒
        timeout: 100 * 1000, // 5 秒
        // 上传之前触发
        onBeforeUpload(file: any) {
          // file 选中的文件，格式如 { key: file }
          // console.log('onBeforeUpload', file)
          return file
        },
        // 上传进度的回调函数
        onProgress(_progress: number) {
          // progress 是 0-100 的数字
          // console.log('progress', progress)
        },
        // 单个文件上传成功之后
        onSuccess(file: File, _res: any) {
          useMessage.success(`${file.name} 上传成功`, { closable: true })
        },
        // 单个文件上传失败
        onFailed(file: File, res: any) {
          useMessage.error(`${file.name} 上传失败：${res?.msg ? res.msg : res?.data}`, { closable: true })
        },
        // 上传错误，或者触发 timeout 超时
        onError(file: File, err: any, _res: any) {
          useMessage.error(`${file.name} 上传出错:${err}`, { closable: true })
        },
        // 自定义插入图片
        customInsert(res: any, insertFn: InsertFnType) {
          // res 即服务端的返回结果
          const url = addHost(res.data) ?? ''

          // 从 res 中找到 url alt href ，然后插图图片
          insertFn(url, '图片', url)
        },
      },
    },
    hoverbarKeys,
  }

  function customPaste(editor: IDomEditor, event: ClipboardEvent): boolean {
    // event 是 ClipboardEvent 类型，可以拿到粘贴的数据
    // 可参考 https://developer.mozilla.org/zh-CN/docs/Web/API/ClipboardEvent
    const text = event?.clipboardData?.getData('text/plain') // 获取粘贴的纯文本
    if (text)
      editor.insertText(text)

    // 阻止默认的粘贴行为
    event.preventDefault()
    return false

    // 继续执行默认的粘贴行为
    // return true
  }

  // 自定义校验链接
  /**
   * 返回值有三种选择：
   * 1. 返回 true ，说明检查通过，编辑器将正常插入链接
   * 2. 返回一个字符串，说明检查未通过，编辑器会阻止插入。会 alert 出错误信息（即返回的字符串）
   * 3. 返回 undefined（即没有任何返回），说明检查未通过，编辑器会阻止插入。但不会提示任何信息
   */
  function customCheckLinkFn(text: string, url: string): string | boolean | undefined {
    // console.log('customCheckLinkFn')
    let isTrue = false
    let msg = ''
    if (!url)
      return

    if (url.indexOf('http') !== 0) {
      msg = '链接必须以 http/https 开头'
      return msg
    }
    const regs = AllowHostList
    regs.push(window.location.host)
    const urlReg = /^(http(s)?:\/\/)(([0-9a-zA-Z-]{1,})+\.[a-zA-Z]{2,})/
    const host = url.match(urlReg)?.[3]
    if (host) {
      // console.log('host', host)
      for (const i of regs) {
        if (i.match(host)) {
          isTrue = true
          break
        }
      }
    }
    if (!isTrue) {
      msg = `链接域名必须${AllowHostList.filter(v => v).join('、')}`
      return msg
    }
    return isTrue
  }

  // 自定义转换链接 url
  function customParseLinkUrl(url: string): string {
    if (url.indexOf('http') !== 0)
      return `http://${url}`

    return url
  }

  function removeImgHost(str: string): string {
    let newStr = str
    if (!isString(str))
      return newStr

    newStr = contentRemoveHost(str)
    return newStr || str
  }

  return {
    toolbarConfig,
    editorConfig,
    hoverbarKeys,

    customPaste,
    customCheckLinkFn,
    customParseLinkUrl,
    removeImgHost,
  }
}
